import React, { useCallback, useEffect, useRef, useState } from "react";

// ヒートマップ: 実際のページを枠内（iframe・同じオリジン）に表示し、そのページの上にクリックの密度を描いた
// canvas を差し込む（枠の中でスクロールしても点がページに付いてくる）。
// 位置は「押した要素」を枠内のページで探して合わせる（画面幅が違っても要素に付いてくる）。
// 見つからない要素は、ページ幅に対する割合と高さの比で置く。

export interface HeatmapData {
  path: string;
  layout: "pc" | "sp";
  pageviews: number;
  clicks: { x: number; y: number; dw: number; dh: number; sel: string; ox: number; oy: number }[];
  elements: { sel: string; label: string; target: string; href: string; count: number }[];
  scroll: { depth: number; percent: number }[];
  scroll_samples: number;
}

interface Props {
  data: HeatmapData;
}

// ページを表示する画面の大きさ（サイトの表示の切り替わり 768px をまたがないように選ぶ）
const FRAME = { pc: { w: 1280, h: 800 }, sp: { w: 390, h: 760 } } as const;
const RADIUS = 22;
const OVERLAY_ID = "__an_heatmap_overlay";
const HIGHLIGHT_ID = "__an_heatmap_highlight";
// canvas の画素数の上限（長いページでも重くならないよう、解像度を落として描く）
const MAX_PIXELS = 6_000_000;

const TARGET_LABEL: Record<string, string> = {
  tel: "電話",
  line: "LINE",
  mail: "メール",
  map: "地図",
  external: "外部リンク",
  internal: "ページ移動",
  button: "ボタン",
  form: "フォーム",
  "": "そのほか",
};

// 密度を 1 色（オレンジ）の濃淡にする: 薄い = 少ない、濃い = 多い
const HEAT_RAMP = [
  [251, 211, 193],
  [244, 160, 124],
  [235, 104, 52],
  [196, 72, 25],
  [140, 46, 12],
];

function heatColor(t: number): [number, number, number] {
  const p = Math.min(1, Math.max(0, t)) * (HEAT_RAMP.length - 1);
  const i = Math.floor(p);
  const f = p - i;
  const a = HEAT_RAMP[i];
  const b = HEAT_RAMP[Math.min(i + 1, HEAT_RAMP.length - 1)];
  return [0, 1, 2].map((k) => Math.round(a[k] + (b[k] - a[k]) * f)) as [number, number, number];
}

// スクロール到達率の帯（1 色・青の濃淡。多く見られている所ほど濃い）
const REACH_RAMP = ["#e8f1fd", "#cde2fb", "#9ec5f4", "#6da7ec", "#3987e5", "#256abf"];
const reachStep = (pct: number) => Math.min(REACH_RAMP.length - 1, Math.floor((pct / 100) * REACH_RAMP.length));

export const HeatmapView: React.FC<Props> = ({ data }) => {
  const outerRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [outerWidth, setOuterWidth] = useState(900);
  const [placed, setPlaced] = useState({ matched: 0, total: 0 });
  const [ready, setReady] = useState(0);

  const frame = FRAME[data.layout];
  const bandW = 48;
  const scale = Math.min(1, (outerWidth - bandW - 12) / frame.w);

  useEffect(() => {
    const el = outerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setOuterWidth(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const frameDoc = () => frameRef.current?.contentDocument ?? null;
  const frameWin = () => frameRef.current?.contentWindow ?? null;

  const draw = useCallback(() => {
    const doc = frameDoc();
    const win = frameWin();
    if (!doc || !win) return;
    const docW = Math.max(doc.documentElement.scrollWidth, frame.w);
    const docH = Math.max(doc.documentElement.scrollHeight, doc.body?.scrollHeight ?? 0, frame.h);
    const res = Math.min(1, Math.sqrt(MAX_PIXELS / (docW * docH)));

    let canvas = doc.getElementById(OVERLAY_ID) as HTMLCanvasElement | null;
    if (!canvas) {
      canvas = doc.createElement("canvas");
      canvas.id = OVERLAY_ID;
      canvas.setAttribute("aria-hidden", "true");
      Object.assign(canvas.style, {
        position: "absolute",
        left: "0",
        top: "0",
        pointerEvents: "none",
        zIndex: "2147483646",
      });
      doc.documentElement.appendChild(canvas);
    }
    canvas.style.width = `${docW}px`;
    canvas.style.height = `${docH}px`;
    canvas.width = Math.round(docW * res);
    canvas.height = Math.round(docH * res);
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // 1 回目: 黒の透明度で密度を足し合わせる
    const acc = document.createElement("canvas");
    acc.width = canvas.width;
    acc.height = canvas.height;
    const actx = acc.getContext("2d");
    if (!actx) return;
    const cache = new Map<string, Element | null>();
    const alpha = Math.min(0.35, Math.max(0.05, 3 / Math.sqrt(data.clicks.length + 1)));
    const r = RADIUS * res;
    let matched = 0;
    for (const c of data.clicks) {
      let el = cache.get(c.sel);
      if (el === undefined) {
        try {
          el = c.sel ? doc.querySelector(c.sel) : null;
        } catch {
          el = null; // 古い記録のセレクタが今のページに合わない
        }
        cache.set(c.sel, el);
      }
      const rect = el?.getBoundingClientRect();
      let px: number;
      let py: number;
      if (rect && rect.width > 0 && rect.height > 0) {
        px = rect.left + win.scrollX + c.ox * rect.width;
        py = rect.top + win.scrollY + c.oy * rect.height;
        matched++;
      } else {
        px = c.x * docW;
        py = c.dh > 0 ? (c.y / c.dh) * docH : c.y;
      }
      const g = actx.createRadialGradient(px * res, py * res, 0, px * res, py * res, r);
      g.addColorStop(0, `rgba(0,0,0,${alpha})`);
      g.addColorStop(1, "rgba(0,0,0,0)");
      actx.fillStyle = g;
      actx.fillRect(px * res - r, py * res - r, r * 2, r * 2);
    }
    setPlaced({ matched, total: data.clicks.length });
    if (data.clicks.length === 0) return;

    // 2 回目: 足し合わせた濃さを、1 色の濃淡に置き換える
    const img = actx.getImageData(0, 0, acc.width, acc.height);
    const px = img.data;
    let maxA = 1;
    for (let i = 3; i < px.length; i += 4) maxA = Math.max(maxA, px[i]);
    for (let i = 0; i < px.length; i += 4) {
      const a = px[i + 3];
      if (a === 0) continue;
      const t = a / maxA;
      const [cr, cg, cb] = heatColor(t);
      px[i] = cr;
      px[i + 1] = cg;
      px[i + 2] = cb;
      px[i + 3] = Math.round(Math.min(0.85, 0.25 + t * 0.6) * 255);
    }
    ctx.putImageData(img, 0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, frame.w, frame.h]);

  // 枠内のページでリンクやボタンを押しても移動・送信しないようにする（見るだけ）
  const lockFrame = () => {
    const doc = frameDoc();
    if (!doc) return;
    const stop = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };
    doc.addEventListener("click", stop, true);
    doc.addEventListener("submit", stop, true);
  };

  // 読み込み後、画像などで高さが変わるのを待ってから描く
  const onLoad = () => {
    lockFrame();
    draw();
    window.setTimeout(() => setReady((n) => n + 1), 1500);
  };

  useEffect(() => {
    if (ready > 0) draw();
  }, [draw, ready]);

  const showElement = (sel: string) => {
    const doc = frameDoc();
    const win = frameWin();
    doc?.getElementById(HIGHLIGHT_ID)?.remove();
    if (!doc || !win || !sel) return;
    let el: Element | null = null;
    try {
      el = doc.querySelector(sel);
    } catch {
      return;
    }
    if (!el) return;
    el.scrollIntoView({ block: "center" });
    const rect = el.getBoundingClientRect();
    const box = doc.createElement("div");
    box.id = HIGHLIGHT_ID;
    Object.assign(box.style, {
      position: "absolute",
      left: `${rect.left + win.scrollX - 4}px`,
      top: `${rect.top + win.scrollY - 4}px`,
      width: `${rect.width + 8}px`,
      height: `${rect.height + 8}px`,
      outline: "3px solid #2a78d6",
      borderRadius: "4px",
      pointerEvents: "none",
      zIndex: "2147483647",
    });
    doc.documentElement.appendChild(box);
  };

  return (
    <div className="an-heatmap">
      <div className="an-heatmap-meta">
        <span>
          表示 <strong>{data.pageviews.toLocaleString("ja-JP")}</strong> 回・クリック{" "}
          <strong>{data.clicks.length.toLocaleString("ja-JP")}</strong> 回
        </span>
        <span className="an-heat-legend" aria-label="クリックの多さの凡例">
          少ない
          <span className="an-heat-legend-bar" aria-hidden="true" />
          多い
        </span>
        <button type="button" className="an-btn" onClick={() => setReady((n) => n + 1)}>
          描き直す
        </button>
      </div>
      {placed.total > 0 && placed.matched < placed.total && (
        <p className="an-muted">
          {placed.total - placed.matched} 件は、ページの作りが変わったなどで押した要素が見つからず、おおよその位置に表示しています。
        </p>
      )}

      <div className="an-heatmap-body">
        <div ref={outerRef} className="an-heatmap-stage">
          <div className="an-heatmap-frame" style={{ width: frame.w * scale, height: frame.h * scale }}>
            <iframe
              ref={frameRef}
              key={`${data.path}-${data.layout}`}
              src={data.path}
              title={`${data.path} のヒートマップ`}
              width={frame.w}
              height={frame.h}
              style={{ transform: `scale(${scale})` }}
              onLoad={onLoad}
              sandbox="allow-same-origin allow-scripts"
            />
          </div>
          {/* スクロール到達率: ページを 10 等分し、その深さまで見た表示の割合 */}
          <div className="an-scroll-band" style={{ width: bandW, height: frame.h * scale }} aria-hidden="true">
            {data.scroll.map((s) => (
              <div
                key={s.depth}
                className="an-scroll-cell"
                style={{
                  top: `${s.depth - 10}%`,
                  background: data.scroll_samples > 0 ? REACH_RAMP[reachStep(s.percent)] : "var(--grid)",
                  color: reachStep(s.percent) >= 4 ? "#ffffff" : "#0b0b0b",
                }}
                title={`ページの ${s.depth}% まで見た割合: ${s.percent.toFixed(0)}%`}
              >
                {data.scroll_samples > 0 ? `${s.percent.toFixed(0)}%` : ""}
              </div>
            ))}
          </div>
        </div>

        <aside className="an-heatmap-side">
          <h3>よく押された所</h3>
          {data.elements.length === 0 ? (
            <p className="an-muted">この期間のクリックはまだありません。</p>
          ) : (
            <table className="an-table">
              <thead>
                <tr>
                  <th>押した所（行に合わせると枠内で示します）</th>
                  <th className="num">回数</th>
                </tr>
              </thead>
              <tbody>
                {data.elements.map((e) => (
                  <tr key={e.sel} tabIndex={0} onMouseEnter={() => showElement(e.sel)} onFocus={() => showElement(e.sel)}>
                    <td>
                      <span className={`an-tag an-tag-${e.target || "other"}`}>{TARGET_LABEL[e.target] ?? e.target}</span>{" "}
                      {e.label || <span className="an-muted">（文字なし）</span>}
                      {e.href && <div className="an-muted an-ellipsis">{e.href}</div>}
                    </td>
                    <td className="num">{e.count.toLocaleString("ja-JP")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <h3>スクロール到達率</h3>
          <p className="an-muted">
            ページのどこまで見られたか（枠の右の帯。上がページの先頭）。{data.scroll_samples.toLocaleString("ja-JP")} 回の表示から集計。
          </p>
          <table className="an-table">
            <thead>
              <tr>
                <th>ページの深さ</th>
                <th className="num">そこまで見た割合</th>
              </tr>
            </thead>
            <tbody>
              {data.scroll.map((s) => (
                <tr key={s.depth}>
                  <td>{s.depth}%</td>
                  <td className="num">{data.scroll_samples > 0 ? `${s.percent.toFixed(0)}%` : "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </aside>
      </div>
    </div>
  );
};
